'use client';

import { useTranslations, useLocale } from 'next-intl';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { locales } from '@/lib/i18n';

export default function Footer() {
  const t = useTranslations('footer');
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();

  const switchLanguage = (newLocale: string) => {
    // Remove current locale from pathname
    const pathWithoutLocale = pathname.replace(`/${locale}`, '') || '/';
    const newPath = `/${newLocale}${pathWithoutLocale}`;
    router.push(newPath);
  };

  const withLocale = (path: string) => (path === '/' ? `/${locale}` : `/${locale}${path}`);

  return (
    <footer className="bg-[#632563] text-white relative" style={{ zIndex: 20 }}>
      <div className="container-max px-4 sm:px-6 lg:px-8 py-12">
        {/* Centered Logo */}
        <div className="flex justify-center mb-8">
          <Image
            src="/images/logo-right6.png"
            alt="Scoil Colmcille Naofa Logo"
            width={200}
            height={64}
            className="h-16 w-auto object-contain"
            priority
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold">{t('quickLinks')}</h4>
            <ul className="space-y-2">
              <li>
                <Link 
                  href={withLocale('/about')} 
                  className="text-gray-300 hover:text-white transition-colors duration-200"
                >
                  {t('about')}
                </Link>
              </li>
              <li>
                <Link 
                  href={withLocale('/notices')} 
                  className="text-gray-300 hover:text-white transition-colors duration-200"
                >
                  {t('notices')}
                </Link>
              </li>
              <li>
                <Link 
                  href={withLocale('/contact')} 
                  className="text-gray-300 hover:text-white transition-colors duration-200"
                >
                  {t('contact')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold">{t('contactInformation')}</h4>
            <div className="space-y-3 text-sm text-gray-300">
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-[#97c472] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <div>
                  <p className="font-medium">{t('address')}</p>
                  <p>BAILE NA FINNE CO DHUN NA NGALL</p>
                  <p>Donegal West, Ulster</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5 text-[#97c472] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <div>
                  <p className="font-medium">{t('phone')}</p>
                  <p>074 9546209</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5 text-[#97c472] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <div>
                  <p className="font-medium">{t('email')}</p>
                  <a 
                    href="mailto:sncolmcille@gmail.com" 
                    className="hover:text-white transition-colors duration-200"
                  >
                    sncolmcille@gmail.com
                  </a>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5 text-[#97c472] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <div>
                  <p className="font-medium">{t('principal')}</p>
                  <p>Seaghan Mac Laifeartai</p>
                </div>
              </div>
            </div>
          </div>

          {/* Language */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold">{t('language')}</h4>
            <div className="space-y-2">
              <button
                onClick={() => switchLanguage(locale === 'en' ? 'ga' : 'en')}
                className="text-gray-300 hover:text-white transition-colors duration-200 font-medium"
                aria-label={`Switch to ${locale === 'en' ? 'Irish' : 'English'}`}
              >
                Gaeilge ↔ English
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-sm text-gray-400">
                {t('copyright')}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Designed by GaelDev
              </p>
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-gray-400">
              <Link 
                href={withLocale('/privacy')}
                className="hover:text-white transition-colors duration-200"
              >
                {t('privacyPolicy')}
              </Link>
              <Link 
                href={withLocale('/accessibility')}
                className="hover:text-white transition-colors duration-200"
              >
                {t('accessibility')}
              </Link>
              <Link 
                href={withLocale('/sitemap')}
                className="hover:text-white transition-colors duration-200"
              >
                {t('sitemap')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
